import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { providerConfigApi } from '../api/generated/providerConfig.api.js';
import { providerFeatureFlagsApi } from '../api/generated/providerFeatureFlags.api.js';
import type {
  FeatureFlag,
  ProviderConfigSection,
  ProviderConfigSectionResponse,
  ProviderSystemInfoResponse,
} from '@cnapse/shared/schemas.js';

/**
 * Provider Configuration types
 * Note: Platform, Stripe, Database, Security, Storage, Monitoring moved to AWS Parameter Store
 */
export type ConfigSection = ProviderConfigSection;

export interface ProviderConfig {
  [key: string]: unknown;
}

export type { FeatureFlag };

export interface SystemInfo {
  database: {
    host: string;
    port: number;
    name: string;
    ssl: boolean;
    maxConnections: number;
    connectionTimeout: number;
    idleTimeout: number;
    statementTimeout: number;
  };
}

/**
 * Provider Settings Context State
 */
interface ProviderSettingsState {
  // Configuration
  config: Record<ConfigSection, ProviderConfig>;
  configLoading: boolean;
  configError: string | null;

  // System Information
  systemInfo: SystemInfo | null;
  systemInfoLoading: boolean;
  systemInfoError: string | null;

  // Feature Flags
  featureFlags: FeatureFlag[];
  flagsLoading: boolean;
  flagsError: string | null;

  // Cache management
  lastFetched: Record<string, Date>;
}

/**
 * Provider Settings Context Methods
 */
interface ProviderSettingsContextValue extends ProviderSettingsState {
  // Config methods
  fetchConfigSection: (section: ConfigSection) => Promise<void>;
  updateConfigValue: (
    section: ConfigSection,
    key: string,
    value: unknown,
    encrypt?: boolean
  ) => Promise<void>;
  updateConfigSection: (
    section: ConfigSection,
    values: Record<string, unknown>,
    sensitiveKeys?: string[]
  ) => Promise<void>;

  // System info methods
  fetchSystemInfo: () => Promise<void>;

  // Feature flag methods
  fetchFeatureFlags: () => Promise<void>;
  createFeatureFlag: (
    flagKey: string,
    flagName: string,
    description?: string
  ) => Promise<void>;
  toggleFeatureFlag: (flagKey: string, enabled: boolean) => Promise<void>;
  initializeDefaultFlags: () => Promise<void>;

  // Cache management
  clearCache: () => void;
  refreshAll: () => Promise<void>;
}

const ProviderSettingsContext = createContext<
  ProviderSettingsContextValue | undefined
>(undefined);

/**
 * Provider Settings Provider Component
 */
export function ProviderSettingsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ProviderSettingsState>({
    config: {} as Record<ConfigSection, ProviderConfig>,
    configLoading: false,
    configError: null,

    systemInfo: null,
    systemInfoLoading: false,
    systemInfoError: null,

    featureFlags: [],
    flagsLoading: false,
    flagsError: null,

    lastFetched: {},
  });

  /**
   * Fetch configuration section
   */
  const fetchConfigSection = useCallback(async (section: ConfigSection) => {
    setState((prev) => ({ ...prev, configLoading: true, configError: null }));

    try {
      const response = await providerConfigApi.getSection({ section }) as ProviderConfigSectionResponse;

      if (response.success) {
        setState((prev) => ({
          ...prev,
          config: {
            ...prev.config,
            [section]: response.data.config,
          },
          configLoading: false,
          lastFetched: {
            ...prev.lastFetched,
            [`config_${section}`]: new Date(),
          },
        }));
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        configLoading: false,
        configError: error instanceof Error ? error.message : 'Failed to load configuration',
      }));
    }
  }, []);

  /**
   * Update single config value
   */
  const updateConfigValue = useCallback(
    async (
      section: ConfigSection,
      key: string,
      value: unknown,
      encrypt = false
    ) => {
      try {
        await providerConfigApi.setValue({
          section,
          key,
          value,
          encrypt,
        });

        await fetchConfigSection(section);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to update configuration'
        );
      }
    },
    [fetchConfigSection]
  );

  /**
   * Fetch system information
   */
  const fetchSystemInfo = useCallback(async () => {
    setState((prev) => ({ ...prev, systemInfoLoading: true, systemInfoError: null }));

    try {
      const response = await providerConfigApi.getSystemInfo() as ProviderSystemInfoResponse;

      setState((prev) => ({
        ...prev,
        systemInfo: response,
        systemInfoLoading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch system information';

      setState((prev) => ({
        ...prev,
        systemInfoLoading: false,
        systemInfoError: errorMessage,
      }));

      console.error('Failed to fetch system info:', error);
    }
  }, []);

  /**
   * Update entire config section (batch)
   */
  const updateConfigSection = useCallback(
    async (
      section: ConfigSection,
      values: Record<string, unknown>,
      sensitiveKeys: string[] = []
    ) => {
      try {
        await providerConfigApi.setSection({
          section,
          values,
          sensitiveKeys,
        });

        await fetchConfigSection(section);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to update configuration'
        );
      }
    },
    [fetchConfigSection]
  );

  /**
   * Fetch all feature flags
   */
  const fetchFeatureFlags = useCallback(async () => {
    setState((prev) => ({ ...prev, flagsLoading: true, flagsError: null }));

    try {
      const data = await providerFeatureFlagsApi.list() as { flags: FeatureFlag[]; count: number };

      setState((prev) => ({
        ...prev,
        featureFlags: data.flags,
        flagsLoading: false,
        lastFetched: {
          ...prev.lastFetched,
          feature_flags: new Date(),
        },
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        flagsLoading: false,
        flagsError:
          error instanceof Error ? error.message : 'Failed to fetch feature flags',
      }));
    }
  }, []);

  /**
   * Create new feature flag
   */
  const createFeatureFlag = useCallback(
    async (flagKey: string, flagName: string, description?: string) => {
      try {
        await providerFeatureFlagsApi.create({
          flagKey,
          flagName,
          description,
          isEnabled: false,
        });

        await fetchFeatureFlags();
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to create feature flag'
        );
      }
    },
    [fetchFeatureFlags]
  );

  /**
   * Toggle feature flag globally
   */
  const toggleFeatureFlag = useCallback(
    async (flagKey: string, enabled: boolean) => {
      try {
        if (enabled) {
          await providerFeatureFlagsApi.enable({ flagKey });
        } else {
          await providerFeatureFlagsApi.disable({ flagKey });
        }

        await fetchFeatureFlags();
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to toggle feature flag'
        );
      }
    },
    [fetchFeatureFlags]
  );

  /**
   * Initialize default feature flags
   */
  const initializeDefaultFlags = useCallback(async () => {
    try {
      await providerFeatureFlagsApi.initialize();
      await fetchFeatureFlags();
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to initialize feature flags'
      );
    }
  }, [fetchFeatureFlags]);

  /**
   * Clear all cached data
   */
  const clearCache = useCallback(() => {
    setState((prev) => ({
      ...prev,
      config: {} as Record<ConfigSection, ProviderConfig>,
      featureFlags: [],
      lastFetched: {},
    }));
  }, []);

  /**
   * Refresh all data
   */
  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchFeatureFlags(),
    ]);
  }, [fetchFeatureFlags]);

  const value: ProviderSettingsContextValue = {
    ...state,
    fetchConfigSection,
    updateConfigValue,
    updateConfigSection,
    fetchSystemInfo,
    fetchFeatureFlags,
    createFeatureFlag,
    toggleFeatureFlag,
    initializeDefaultFlags,
    clearCache,
    refreshAll,
  };

  return (
    <ProviderSettingsContext.Provider value={value}>
      {children}
    </ProviderSettingsContext.Provider>
  );
}

/**
 * Hook to access provider settings context
 */
export function useProviderSettings(): ProviderSettingsContextValue {
  const context = useContext(ProviderSettingsContext);
  if (!context) {
    throw new Error('useProviderSettings must be used within ProviderSettingsProvider');
  }
  return context;
}
